import { queryOptions } from '@tanstack/react-query';
import { api } from '../../lib/api';

export interface HolidayRow {
  readonly id: string;
  /** Null for a company day somebody added by hand. */
  readonly calendarId: string | null;
  readonly onDate: string;
  readonly name: string;
  /** Derived from a lunar rule; the announced date may differ by a day or two. */
  readonly estimated: boolean;
  readonly observed: boolean;
  readonly source: 'generated' | 'manual';
}

export interface HolidayCalendarRow {
  readonly id: string;
  readonly country: string;
  readonly subdivision: string | null;
  readonly label: string;
  readonly enabled: boolean;
  readonly holidayCount: number;
}

function base(workspaceId: string): string {
  return `/workspaces/${encodeURIComponent(workspaceId)}/calendars`;
}

/**
 * Every holiday the workspace observes.
 *
 * Unbounded on purpose — a workspace has a couple of hundred of these across
 * every year it has configured, and the alternative is refetching whenever a
 * sprint column scrolls into a different month. `staleTime` is long because
 * public holidays are the least volatile data in the product.
 */
export const holidaysQueryOptions = (workspaceId: string) =>
  queryOptions({
    queryKey: ['ws', workspaceId, 'holidays'] as const,
    queryFn: () => api.get<HolidayRow[]>(`${base(workspaceId)}/holidays`),
    staleTime: 60 * 60 * 1000,
  });

export const holidayCalendarsQueryOptions = (workspaceId: string) =>
  queryOptions({
    queryKey: ['ws', workspaceId, 'calendars'] as const,
    queryFn: () => api.get<HolidayCalendarRow[]>(base(workspaceId)),
  });

export function createCalendar(
  workspaceId: string,
  input: { country: string; subdivision?: string | null },
): Promise<HolidayCalendarRow> {
  return api.post<HolidayCalendarRow>(base(workspaceId), input);
}

export function deleteCalendar(workspaceId: string, calendarId: string): Promise<void> {
  return api.delete<void>(`${base(workspaceId)}/${encodeURIComponent(calendarId)}`);
}

export function generateYear(
  workspaceId: string,
  calendarId: string,
  year: number,
): Promise<{ inserted: number; kept: number }> {
  return api.post(`${base(workspaceId)}/${encodeURIComponent(calendarId)}/generate`, { year });
}

export function addHoliday(
  workspaceId: string,
  input: { onDate: string; name: string },
): Promise<HolidayRow> {
  return api.post<HolidayRow>(`${base(workspaceId)}/holidays`, input);
}

/** Correct a moon-sighted date, or say the team works that day after all. */
export function editHoliday(
  workspaceId: string,
  holidayId: string,
  input: { onDate?: string; observed?: boolean },
): Promise<HolidayRow> {
  return api.patch<HolidayRow>(`${base(workspaceId)}/holidays/${encodeURIComponent(holidayId)}`, input);
}

export function deleteHoliday(workspaceId: string, holidayId: string): Promise<void> {
  return api.delete<void>(`${base(workspaceId)}/holidays/${encodeURIComponent(holidayId)}`);
}
